import React, { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Link,
  Skeleton,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import styles from "./styles";
import moment from "moment";
import { inspectionStatusStyleArray } from "../../../config";
import { getApiData } from "../../../../../utils/APIHelper";
import ImageGallery from "./ImageGallery";
import { ReactComponent as PdfIcon } from "../../../../../assets/svg/PdfIcon.svg";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function InspectionDetailsModal({ selectedRow, setSelectedRow }) {
  const [details, setDetails] = useState({});
  const [loader, setLoader] = useState(false);
  const [projectFiles, setProjectFiles] = useState({
    inspectionReport: null,
    supportingImages: [],
    supportingFiles: [],
    loading: true,
  });

  const handleCloseModal = () => {
    setSelectedRow(null);
    setDetails({});
  };

  useEffect(() => {
    if (selectedRow?.id) {
      setLoader(true);
      getInspectionInfo();
    }
  }, [selectedRow]);

  async function getInspectionInfo() {
    try {
      const response = await getApiData(
        `api/v2/project/inspection/${selectedRow?.id}`,
        "GET",
        {},
        {},
        true
      );
      if (response) {
        setDetails(response);
        const documents = response?.documents || [];
        setProjectFiles({
          inspectionReport: documents.filter(
            (item) => item.tags === "INSPECTION-REPORT"
          )[0],
          supportingImages: documents.filter(
            (item) =>
              item.tags == "SUPPORTING-DOCS" && item.extention.includes("image")
          ),
          supportingFiles: documents.filter(
            (item) =>
              item.tags == "SUPPORTING-DOCS" &&
              !item.extention.includes("image")
          ),
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error fetching inspection info:", error);
    } finally {
      setLoader(false);
    }
  }

  return (
    <Dialog
      open={!!selectedRow}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          position: "relative",
        }}
      >
        <div style={{ position: "relative", width: "100%" }}>
          <h2
            style={{
              ...styles.projectHeading,
              padding: "2vh 2vw 0 2vw",
              letterSpacing: "0",
            }}
          >
            Inspection Details
          </h2>
        </div>
        <IconButton
          onClick={handleCloseModal}
          style={{ position: "absolute", top: 5, right: 5 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent
        style={{
          ...styles.columnFlex,
          ...styles.container,
          padding: "2vh 2vw 4vh 2vw",
          maxHeight: "60vh",
          minHeight: "55vh",
          overflowY: "scroll",
          scrollbarWidth: "none",
        }}
      >
        {loader ? (
          <center>
            <CircularProgress />
          </center>
        ) : (
          <>
            <div style={{ ...styles.columnFlex }}>
              <h2 style={styles.modal_heading}>{details?.milestoneName}</h2>
              <p style={styles.modal_desc}>{details?.groupName}</p>
            </div>
            <div
              style={{ ...styles.columnFlex, gap: "15px", marginTop: "2vh" }}
            >
              <div style={styles.section_heading}>Details</div>
              <div style={{ ...styles.columnFlex, gap: "15px" }}>
                <div
                  style={{ ...styles.rowFlex, justifyContent: "space-between" }}
                >
                  <p style={styles.modal_desc}>Due Date</p>
                  <p style={styles.modal_value}>
                    {details?.dueDate
                      ? moment(details?.dueDate).format("MMM DD, YYYY")
                      : "-"}
                  </p>
                </div>
                <div
                  style={{ ...styles.rowFlex, justifyContent: "space-between" }}
                >
                  <p style={styles.modal_desc}>Inspection Date</p>
                  <p style={styles.modal_value}>
                    {details?.inspectionDate
                      ? moment(details?.inspectionDate).format("MMM DD, YYYY")
                      : "-"}
                  </p>
                </div>
                <div
                  style={{ ...styles.rowFlex, justifyContent: "space-between" }}
                >
                  <p style={styles.modal_desc}>Result</p>
                  <div
                    style={{
                      ...styles.status_pill,
                      backgroundColor:
                        inspectionStatusStyleArray.find(
                          (style) => style.status === details?.result
                        )?.backgroundColor || "transparent",
                      color:
                        inspectionStatusStyleArray.find(
                          (style) => style.status === details?.result
                        )?.color || "black",
                    }}
                  >
                    {getStatusDisplayText(details?.result)}
                  </div>
                </div>
                <div
                  style={{ ...styles.rowFlex, justifyContent: "space-between" }}
                >
                  <p style={styles.modal_desc}>Notes</p>
                  <p style={styles.modal_value}>{details?.notes || "-"}</p>
                </div>
              </div>
            </div>
            <div
              style={{ ...styles.columnFlex, gap: "15px", marginTop: "2vh" }}
            >
              <div style={styles.section_heading}>Reason</div>
              <div style={{ ...styles.columnFlex, gap: "15px" }}>
                <div
                  style={{ ...styles.rowFlex, justifyContent: "space-between" }}
                >
                  <p style={styles.modal_value}>{details?.reason || "-"}</p>
                </div>
              </div>
            </div>

            <div
              style={{ ...styles.columnFlex, gap: "15px", marginTop: "2vh" }}
            >
              <div style={styles.section_heading}>Inspection Report</div>
              <div style={{ ...styles.columnFlex, gap: "15px" }}>
                {projectFiles?.inspectionReport ? (
                  <Link
                    href={projectFiles.inspectionReport.path}
                    target="_blank"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      textDecoration: "none",
                      color: "text.primary",
                      padding: "0",
                      borderRadius: 1,
                    }}
                  >
                    <PdfIcon />
                    <p
                      style={{
                        ...styles.modal_value,
                        flexGrow: 1,
                        marginLeft: "10px",
                        fontWeight: "500",
                      }}
                    >
                      Submitted Report.pdf
                    </p>
                  </Link>
                ) : (
                  <p style={{ ...styles.modal_value, fontWeight: "400" }}>
                    No inspection report
                  </p>
                )}
              </div>
            </div>

            <ImageGallery projectFiles={projectFiles} />

            {projectFiles?.supportingFiles?.length > 0 && (
              <div
                style={{ ...styles.columnFlex, gap: "15px", marginTop: "2vh" }}
              >
                <div style={styles.section_heading}>Supporting Files</div>
                <div style={{ ...styles.columnFlex, gap: "15px" }}>
                  {projectFiles.loading ? (
                    <Skeleton variant="rectangular" width="100%" height={50} />
                  ) : (
                    projectFiles?.supportingFiles?.map((file, index) => (
                      <Link
                        key={file.path}
                        href={file.path}
                        target="_blank"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          textDecoration: "none",
                          color: "text.primary",
                          padding: "0",
                          borderRadius: 1,
                        }}
                      >
                        <PdfIcon />
                        <p
                          style={{
                            ...styles.modal_value,
                            flexGrow: 1,
                            marginLeft: "10px",
                            fontWeight: "500",
                          }}
                        >
                          Supporting File {index + 1}
                        </p>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InspectionDetailsModal;
